import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import { Notice } from './components';
import type { Client, ReportLink } from './types';

/**
 * The share links for a client's reports, plus their branding.
 *
 * One link per connected account. These are what get pasted into the client's
 * HubSpot dashboard as embedded content, so the full absolute URL is shown
 * ready to copy rather than the relative path stored server-side.
 */

export function ReportLinks({ client }: { client: Client }) {
  const [links, setLinks] = useState<ReportLink[]>([]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [busy, setBusy] = useState('');

  const [logo, setLogo] = useState(client.logo_url ?? '');
  const [accent, setAccent] = useState(client.accent_color ?? '');
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      setLinks(await api.reportLinks(client.slug));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load report links');
    }
  }, [client.slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const absolute = (path: string) => `${window.location.origin}${path}`;

  const copy = async (path: string) => {
    await navigator.clipboard.writeText(absolute(path));
    setCopied(path);
    setTimeout(() => setCopied(null), 1600);
  };

  const rotate = async (link: ReportLink) => {
    const confirmed = window.confirm(
      `Replace the link for ${link.account_name}?\n\n` +
        'The current URL stops working immediately, so any dashboard already ' +
        'embedding it will show "report not found" until you paste the new one.',
    );
    if (!confirmed) return;
    setBusy(String(link.connection_id));
    try {
      await api.rotateReportLink(link.connection_id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not rotate the link');
    } finally {
      setBusy('');
    }
  };

  const saveBranding = async () => {
    setBusy('branding');
    try {
      await api.setBranding(client.slug, {
        logo_url: logo.trim() || null,
        accent_color: accent.trim() || null,
      });
      setSaved(true);
      setError('');
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save branding');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="card">
      <h2>Client reports</h2>
      <p className="muted" style={{ marginTop: -6 }}>
        One report per connected account. Paste a link into the client&rsquo;s HubSpot
        dashboard under <strong>Add content &rarr; External content</strong>. Anyone with
        the URL can read that report, so treat it as published.
      </p>

      {error && <Notice kind="err">{error}</Notice>}

      {links.length === 0 ? (
        <p className="muted">No accounts linked yet, so there is nothing to report on.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Account</th>
                <th>Report URL</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.connection_id}>
                  <td>
                    {link.account_name}
                    <div className="muted">{link.platform}</div>
                  </td>
                  <td className="mono">{absolute(link.path)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button onClick={() => void copy(link.path)}>
                      {copied === link.path ? 'Copied' : 'Copy'}
                    </button>{' '}
                    <a href={link.path} target="_blank" rel="noreferrer">
                      <button>Open</button>
                    </a>{' '}
                    <button
                      disabled={busy === String(link.connection_id)}
                      onClick={() => void rotate(link)}
                    >
                      {busy === String(link.connection_id) ? 'Replacing…' : 'Replace link'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Branding</h3>
      <p className="muted" style={{ marginTop: -6 }}>
        Applied to every report for this client. Leave both blank for a neutral look.
      </p>
      <div className="form-row">
        <label>
          Logo URL
          <input
            value={logo}
            placeholder="/branding/altitude_accelerator.png"
            onChange={(e) => setLogo(e.target.value)}
          />
        </label>
        <label>
          Accent colour
          <input value={accent} placeholder="#E8CE04" onChange={(e) => setAccent(e.target.value)} />
        </label>
      </div>
      <div style={{ marginTop: 12 }}>
        <button disabled={busy === 'branding'} onClick={() => void saveBranding()}>
          {busy === 'branding' ? 'Saving…' : saved ? 'Saved' : 'Save branding'}
        </button>
      </div>
    </div>
  );
}
